import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import ContactModal from '../Modals/ContactModal/ContactModal'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { ReleasesService } from 'services'
import useReleaseActions from '../../hooks/useReleaseActions'
import { MaterialIcon } from '@dsny/dsny-component-library'
import {
  Mail,
  FlagFilled,
  FlagUnfilled,
  ArchiveFilled,
  ArchiveUnfilled
} from '@dsny/dsny-component-library/icons'
import { Tonality } from '@dsny/dsny-component-library'
import styles from '../../../../components/Dropdowns/Dropdowns.module.scss'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const ActionsDropdown = ({ release, setRelease }) => {
  const { t } = useTranslation()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const { currentReleaseInformation } = useSelector((state) => state.releases)
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Use the custom hook for release actions
  const {
    handleFlag,
    handleArchive,
    handleAddToPlaylist,
    createNewPlaylist: createNewPlaylistHook
  } = useReleaseActions(
    release,
    setRelease,
    'release_information_actions_dropdown'
  )

  const openContactModal = () => {
    setIsContactModalOpen(true)
    try {
      ReleasesService.trackCommentingInit(currentReleaseInformation?.releaseid)
    } catch (error) {
      console.log('Track error', error)
    }
    trackEvent('Release Contact', {
      release_id: normalizeValue(currentReleaseInformation?.releaseid),
      artist: normalizeValue(currentReleaseInformation?.artist),
      title: normalizeValue(currentReleaseInformation?.title),
      source: 'release_information_actions_dropdown',
      type: 'contact'
    })
  }

  // Use custom hook for playlist operations - just use the hook's functions directly
  const createNewPlaylist = createNewPlaylistHook

  // Use the hook's function for adding to playlist
  const handleAddToPlaylistLocal = handleAddToPlaylist

  if (!currentReleaseInformation) {
    return null
  }

  return (
    <>
      <DropdownMenu.Root onOpenChange={setIsDropdownOpen}>
        <DropdownMenu.Trigger asChild>
          <Tonality.Button setting="player" variant="text">
            <span style={{ marginRight: '4px' }}>{t('general.more')}</span>
            {isDropdownOpen ? (
              <MaterialIcon
                name="KeyboardArrowUp"
                color={theme.colors.player50}
              />
            ) : (
              <MaterialIcon
                name="KeyboardArrowDown"
                color={theme.colors.player50}
              />
            )}
          </Tonality.Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100
            }}
            className={styles.DropdownMenuContent}
            sideOffset={5}
            avoidCollisions
          >
            {currentReleaseInformation?.commenting_enabled && (
              <DropdownMenu.Item
                className={styles.DropdownMenuItem}
                onClick={openContactModal}
              >
                <Mail
                  width={16}
                  height={16}
                  color={theme.colors.neutralW90}
                  style={{ marginRight: '8px' }}
                />
                {t('release-info.contact')}
              </DropdownMenu.Item>
            )}

            {!(archiveToFilterEnabled && release?.archived) && (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={styles.DropdownMenuItem}>
                  <MaterialIcon name="AddSong" style={{ marginRight: '8px' }} />
                  {t('general.addto')}
                  <div className={styles.RightSlot}>
                    <MaterialIcon
                      name="KeyboardArrowRight"
                      color={theme.colors.neutralW100}
                    />
                  </div>
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent
                    style={{
                      background: theme.colors.neutralW20,
                      color: theme.colors.neutralW100
                    }}
                    className={styles.DropdownMenuContent}
                    sideOffset={2}
                    alignOffset={-5}
                  >
                    {playlistAbstracts?.map((playlist) => {
                      return (
                        <DropdownMenu.Item
                          key={playlist?.playlistid}
                          className={styles.DropdownMenuItem}
                          onClick={() => handleAddToPlaylistLocal(playlist)}
                        >
                          {playlist?.name}
                        </DropdownMenu.Item>
                      )
                    })}

                    <DropdownMenu.Item
                      className={styles.DropdownMenuItem}
                      onClick={createNewPlaylist}
                    >
                      New playlist{' '}
                      <div className={styles.RightSlot}>
                        <MaterialIcon name="Add" />
                      </div>
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            )}

            <DropdownMenu.Item
              className={styles.DropdownMenuItem}
              onClick={handleFlag}
            >
              {release?.flagged ? (
                <FlagFilled
                  width={16}
                  height={16}
                  color={theme.colors.neutralW90}
                  style={{ marginRight: '8px' }}
                />
              ) : (
                <FlagUnfilled
                  width={16}
                  height={16}
                  color={theme.colors.neutralW90}
                  style={{ marginRight: '8px' }}
                />
              )}
              {release?.flagged ? t('general.unflag') : t('general.flag')}
            </DropdownMenu.Item>

            {!isMobile && (
              <DropdownMenu.Item
                className={styles.DropdownMenuItem}
                onClick={handleArchive}
              >
                {release?.archived ? (
                  <ArchiveFilled
                    width={16}
                    height={16}
                    color={theme.colors.neutralW90}
                    style={{ marginRight: '8px' }}
                  />
                ) : (
                  <ArchiveUnfilled
                    width={16}
                    height={16}
                    color={theme.colors.neutralW90}
                    style={{ marginRight: '8px' }}
                  />
                )}
                {release?.archived
                  ? t('general.unarchive')
                  : t('general.archive')}
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Arrow
              className={styles.DropdownMenuArrow}
              style={{ fill: theme.colors.neutralW20 }}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ContactModal
        isContactModalOpen={isContactModalOpen}
        setIsContactModalOpen={setIsContactModalOpen}
      />
    </>
  )
}

export default ActionsDropdown
